import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../i18n';
import { useLocation, useNavigate } from 'react-router-dom';
import { MainLayout, Card, CardBody, TextInput, TextArea, Button, Alert, Spinner, ActionRow, Badge } from '../components';
import { familyTreesAPI, FamilyTreeDto } from '../services/api';
import { useAppNavigation } from '../contexts/AppNavigationContext';
import { brandClasses } from '../theme';
import { trackEvent } from '../services/analytics';

const treeCardClassName = `bg-[#fffaf0]/95 shadow-[0_16px_34px_rgba(95,83,58,0.08)] hover:border-[#b8c48d] hover:shadow-[0_20px_40px_rgba(95,83,58,0.14)] ${brandClasses.warmSurface}`;
const sectionTitleClassName = `mb-4 text-2xl font-bold ${brandClasses.titleText}`;

const formatTreeDate = (value: string | undefined, unknownLabel: string, locale: string) =>
  formatDate(locale, value, { month: 'short', day: 'numeric', year: 'numeric' }, unknownLabel);

const renderTreeCard = (
  tree: FamilyTreeDto,
  onOpen: (treeId: string) => void,
  options: {
    audienceLabel: string;
    roleVariant: 'success' | 'primary';
    membersLabel: string;
    lastUpdatedLabel: string;
    memberCountLabel: string;
    lastUpdatedDateLabel: string;
    viewerRoleLabel: string;
  }
) => (
  <Card key={tree.id} hoverable onClick={() => onOpen(tree.id)} className={treeCardClassName}>
    <CardBody className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={`text-[11px] font-semibold uppercase tracking-[0.24em] ${brandClasses.subtleText}`}>
            {options.audienceLabel}
          </p>
          <h3 className={`mt-2 text-lg font-medium ${brandClasses.primaryText}`}>{tree.name}</h3>
        </div>
        <Badge variant={options.roleVariant} size="sm" className="shrink-0">
          {tree.role || options.viewerRoleLabel}
        </Badge>
      </div>

      {tree.description && (
        <p className={`text-sm leading-6 ${brandClasses.bodyText}`}>{tree.description}</p>
      )}

      <div className="grid grid-cols-1 gap-3 rounded-2xl border border-[#e5dcc7] bg-white/70 px-4 py-3 sm:grid-cols-2">
        <div>
          <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${brandClasses.subtleText}`}>{options.membersLabel}</p>
          <p className={`mt-1 text-sm font-medium ${brandClasses.primaryText}`}>
            {options.memberCountLabel}
          </p>
        </div>
        <div>
          <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${brandClasses.subtleText}`}>{options.lastUpdatedLabel}</p>
          <p className={`mt-1 text-sm font-medium ${brandClasses.primaryText}`}>{options.lastUpdatedDateLabel}</p>
        </div>
      </div>
    </CardBody>
  </Card>
);

export const TreesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [ownedTrees, setOwnedTrees] = useState<FamilyTreeDto[]>([]);
  const [sharedTrees, setSharedTrees] = useState<FamilyTreeDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newTreeName, setNewTreeName] = useState('');
  const [newTreeDescription, setNewTreeDescription] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { clearNavigationState } = useAppNavigation();

  useEffect(() => {
    clearNavigationState();
  }, [clearNavigationState]);

  useEffect(() => {
    const loadTrees = async () => {
      try {
        setIsLoading(true);
        const [ownedRes, sharedRes] = await Promise.all([
          familyTreesAPI.getOwnedTrees(),
          familyTreesAPI.getSharedTrees(),
        ]);
        const owned = ownedRes.data.data || [];
        const shared = sharedRes.data.data || [];
        setOwnedTrees(owned);
        setSharedTrees(shared);
      } catch (err) {
        const errorMessage = (err as any)?.response?.status === 500
          ? t('errors.serverLoad', { ns: 'trees' })
          : t('errors.loadFailed', { ns: 'trees' });
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadTrees();
  }, [t]);

  useEffect(() => {
    const nextInfoMessage = (location.state as { infoMessage?: string } | null)?.infoMessage || '';
    setInfoMessage(nextInfoMessage);
  }, [location.state]);

  const handleCreateTree = async (e: React.FormEvent) => {
    try {
      e.preventDefault();
      setIsCreating(true);
      // Use provided name or default
      const treeName = newTreeName.trim() || t('create.untitledName', { ns: 'trees' });
      const response = await familyTreesAPI.createTree({
        name: treeName,
        description: newTreeDescription,
      });

      // Auto-navigate to the newly created tree's canvas
      trackEvent('tree_created');
      navigate(`/canvas/${response.data.data.id}`);
    } catch (err) {
      setError(t('errors.createFailed', { ns: 'trees' }));
      setIsCreating(false);
    }
  };

  const handleNavigateToTree = (treeId: string) => {
    trackEvent('tree_opened');
    navigate(`/canvas/${treeId}`);
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="relative overflow-hidden rounded-[2rem] border border-[#e1d8c5] bg-[linear-gradient(135deg,#fbf7ed_0%,#f3eedf_48%,#f7f3e6_100%)] px-6 py-24 shadow-[0_24px_60px_rgba(95,83,58,0.08)]">
          <div className="absolute inset-x-0 top-0 h-32 bg-[radial-gradient(circle_at_top,rgba(137,160,88,0.18),transparent_68%)]" />
          <div className="absolute -left-16 top-20 h-48 w-48 rounded-full bg-[#d9e2b2]/30 blur-3xl" />
          <div className="absolute -right-20 bottom-8 h-56 w-56 rounded-full bg-[#d6c09a]/25 blur-3xl" />
          <div className="relative flex flex-col items-center justify-center">
            <Spinner size="lg" className="mb-4" />
            <p className={`text-lg ${brandClasses.bodyText}`}>{t('status.loadingTrees', { ns: 'trees' })}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div
        className="relative overflow-hidden rounded-[2rem] border border-[#e1d8c5] bg-[linear-gradient(135deg,#fbf7ed_0%,#f3eedf_48%,#f7f3e6_100%)] px-6 py-8 shadow-[0_24px_60px_rgba(95,83,58,0.08)] sm:px-8"
        data-testid="trees-page"
      >
        <div className="absolute inset-x-0 top-0 h-44 bg-[radial-gradient(circle_at_top,rgba(137,160,88,0.18),transparent_72%)]" />
        <div className="absolute -left-16 top-20 h-56 w-56 rounded-full bg-[#d9e2b2]/30 blur-3xl" />
        <div className="absolute right-0 top-32 h-64 w-64 rounded-full bg-[#d6c09a]/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-40 w-40 rounded-full bg-[#b7c987]/15 blur-3xl" />

        <div className="relative space-y-8">
          <section className={`rounded-[1.75rem] border bg-white/50 px-6 py-8 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] backdrop-blur-sm ${brandClasses.heroBorder}`}>
            <div className="max-w-3xl space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.32em] text-[#8d7a57]">{t('hero.eyebrow', { ns: 'trees' })}</p>
              <h1 className={`text-3xl font-bold tracking-tight sm:text-4xl ${brandClasses.titleText}`}>
                {t('hero.title', { ns: 'trees' })}
              </h1>
              <p className={`text-base leading-7 ${brandClasses.bodyText}`}>
                {t('hero.description', { ns: 'trees' })}
              </p>
            </div>
          </section>

        {error && (
          <Alert variant="error" message={error} dismissible onDismiss={() => setError('')} />
        )}
        {infoMessage && (
          <Alert variant="info" message={infoMessage} dismissible onDismiss={() => setInfoMessage('')} />
        )}

        {/* Create new tree section */}
        <div>
          {!showCreateForm ? (
            <Button variant="primary" onClick={() => setShowCreateForm(true)} data-testid="create-tree-button">
              {t('create.cta', { ns: 'trees' })}
            </Button>
          ) : (
            <Card className={treeCardClassName}>
              <CardBody className="space-y-4">
                <h2 className={`text-lg font-semibold ${brandClasses.titleText}`}>{t('create.title', { ns: 'trees' })}</h2>
                <form onSubmit={handleCreateTree} className="space-y-4" data-testid="create-tree-form">
                  <TextInput
                    id="treeName"
                    label={t('create.treeNameLabel', { ns: 'trees' })}
                    placeholder={t('create.treeNamePlaceholder', { ns: 'trees' })}
                    value={newTreeName}
                    onChange={(e) => setNewTreeName(e.target.value)}
                    required
                  />

                  <TextArea
                    id="treeDescription"
                    label={t('create.descriptionLabel', { ns: 'trees' })}
                    placeholder={t('create.descriptionPlaceholder', { ns: 'trees' })}
                    value={newTreeDescription}
                    onChange={(e) => setNewTreeDescription(e.target.value)}
                  />

                  <ActionRow className="pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreateForm(false)}
                      disabled={isCreating}
                    >
                      {t('buttons.cancel', { ns: 'common' })}
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      isLoading={isCreating}
                      disabled={isCreating || !newTreeName.trim()}
                    >
                      {t('buttons.create', { ns: 'common' })}
                    </Button>
                  </ActionRow>
                </form>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Owned trees */}
        {ownedTrees.length > 0 && (
          <div>
            <h2 className={sectionTitleClassName}>{t('sections.myTrees', { ns: 'trees' })}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ownedTrees.map((tree) => (
                renderTreeCard(tree, handleNavigateToTree, {
                  audienceLabel: t('card.ownedTree', { ns: 'trees' }),
                  roleVariant: 'success',
                  membersLabel: t('card.membersLabel', { ns: 'trees' }),
                  lastUpdatedLabel: t('card.lastUpdatedLabel', { ns: 'trees' }),
                  memberCountLabel: t('card.memberCount', { count: tree.memberCount, ns: 'trees' }),
                  lastUpdatedDateLabel: formatTreeDate(tree.lastModified, t('card.unknownDate', { ns: 'trees' }), i18n.language),
                  viewerRoleLabel: t('card.viewerRole', { ns: 'trees' }),
                })
              ))}
            </div>
          </div>
        )}

        {/* Shared trees */}
        {sharedTrees.length > 0 && (
          <div>
            <h2 className={sectionTitleClassName}>{t('sections.sharedWithMe', { ns: 'trees' })}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sharedTrees.map((tree) => (
                renderTreeCard(tree, handleNavigateToTree, {
                  audienceLabel: t('card.sharedTree', { ns: 'trees' }),
                  roleVariant: 'primary',
                  membersLabel: t('card.membersLabel', { ns: 'trees' }),
                  lastUpdatedLabel: t('card.lastUpdatedLabel', { ns: 'trees' }),
                  memberCountLabel: t('card.memberCount', { count: tree.memberCount, ns: 'trees' }),
                  lastUpdatedDateLabel: formatTreeDate(tree.lastModified, t('card.unknownDate', { ns: 'trees' }), i18n.language),
                  viewerRoleLabel: t('card.viewerRole', { ns: 'trees' }),
                })
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {ownedTrees.length === 0 && sharedTrees.length === 0 && (
          <div className="py-12 text-center">
            <p className={`text-lg ${brandClasses.bodyText}`}>{t('empty.noTrees', { ns: 'trees' })}</p>
          </div>
        )}
        </div>
      </div>
    </MainLayout>
  );
};
